import { useEffect, useState } from "react";
import {
  BadgeIndianRupee, Briefcase, FileText, Headphones, MapPin, Package, PackageCheck,
  Boxes, Search, Truck, Users, Zap,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { DestinationCard, FinalCta, QuoteTrackButtons, SectionHead, StepsLine, usePageMeta } from "../components/service/ServiceBits";
import api from "../lib/api";
import VideoHeroBg from "../components/service/VideoHeroBg";

const MAP_IMG = "https://static.prod-images.emergentagent.com/jobs/6e217b82-4821-4792-98ef-42642f657e6a/images/2f18fa2504c882790cfed81c9bd664084e8449cd74991a6cbbe5df1063591ba3.jpeg";

const HIGHLIGHTS = [
  { icon: MapPin, v: "30,000+", l: "PIN Codes Served" },
  { icon: Package, v: "Free", l: "Doorstep Pickup" },
  { icon: Truck, v: "Multiple", l: "Courier Partners" },
  { icon: Search, v: "Live", l: "Shipment Tracking" },
  { icon: Users, v: "Personal", l: "Account Manager" },
];

const CITIES = ["Hyderabad", "Mumbai", "Delhi", "Bengaluru", "Chennai", "Kolkata", "Pune", "Ahmedabad", "Kochi", "Jaipur"];

const SERVICES = [
  { icon: FileText, title: "Document Courier", desc: "Professional delivery for important documents.",
    img: "https://images.unsplash.com/photo-1449247666642-264389f5f5b1?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { icon: Package, title: "Parcel Delivery", desc: "Send personal and commercial parcels across India.",
    img: "https://images.unsplash.com/photo-1624137527136-66e631bdaa0e?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { icon: Zap, title: "Express Courier", desc: "For time-sensitive shipments that cannot wait.",
    img: "https://images.pexels.com/photos/12725455/pexels-photo-12725455.jpeg?auto=compress&cs=tinysrgb&w=800" },
  { icon: Truck, title: "Economy Courier", desc: "Cost-effective shipping for regular shipments.",
    img: "https://images.unsplash.com/photo-1771429631978-674f8689aca6?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { icon: Briefcase, title: "Business Courier", desc: "Reliable solutions for regular business shipments.",
    img: "https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { icon: Boxes, title: "Bulk Shipping", desc: "Solutions for high-volume and heavy shipments.",
    img: "https://images.unsplash.com/photo-1549194388-f61be84a6e9e?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
];

const STEPS = [
  { n: "01", title: "Book", desc: "Get an instant quote and book your shipment online." },
  { n: "02", title: "Pickup", desc: "Free doorstep pickup from your home or office." },
  { n: "03", title: "Transit", desc: "Your parcel moves through our courier network." },
  { n: "04", title: "Delivery", desc: "Delivered safely with tracking at every step." },
];

const WHY = [
  { icon: Truck, title: "Multiple Courier Partners", desc: "The right courier for every lane and budget." },
  { icon: BadgeIndianRupee, title: "Competitive Options", desc: "Compare services and pick price or speed." },
  { icon: Package, title: "Free Pickup", desc: "Doorstep collection at your convenience." },
  { icon: Search, title: "Easy Tracking", desc: "Follow every shipment from pickup to delivery." },
  { icon: Headphones, title: "Customer Support", desc: "Real people to help when you need it." },
  { icon: PackageCheck, title: "Smart Allocation", desc: "Technology-enabled courier selection." },
];

export default function DomesticCourier() {
  const [contact, setContact] = useState(null);
  usePageMeta(
    "Domestic Courier Services Across India | IndiaFast",
    "Fast & reliable domestic courier delivery across India — documents, parcels, express and bulk shipping with free pickup, 30,000+ PIN codes and live tracking.",
    "/domestic-courier"
  );
  useEffect(() => { api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="domestic-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy pb-24 pt-32 sm:pt-36" data-testid="domestic-hero">
        <VideoHeroBg mp4="/videos/road.mp4" webm="/videos/road.webm"
                     poster="https://static.prod-images.emergentagent.com/jobs/6e217b82-4821-4792-98ef-42642f657e6a/images/20a8dffe8cf15a77db968b49edea49ef99e52140da8c7e7e28d57eefb9bffb64.jpeg"
                     posterAlt="IndiaFast branded trucks on a highway" badge="Surface Express" chip="AWB IFAST1000002 · Linehaul DEL → BOM" testid="domestic-hero-video" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <p className="animate-fade-up text-xs font-bold uppercase tracking-[0.25em] text-saffron">Domestic Courier</p>
          <h1 className="animate-fade-up stagger-1 mt-3 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Fast &amp; Reliable Courier Delivery <span className="text-saffron">Across India</span>
          </h1>
          <p className="animate-fade-up stagger-2 mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Send documents, parcels and packages across India with convenient pickup, reliable delivery and easy shipment tracking.
          </p>
          <div className="animate-fade-up stagger-3"><QuoteTrackButtons testid="domestic" /></div>
        </div>
      </section>

      {/* QUICK HIGHLIGHTS */}
      <section className="border-b border-slate-200 bg-white" data-testid="domestic-highlights">
        <div className="mx-auto grid max-w-7xl grid-cols-2 px-4 sm:grid-cols-3 sm:px-6 lg:grid-cols-5 lg:divide-x lg:divide-slate-100">
          {HIGHLIGHTS.map((h, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-6" data-testid={`domestic-highlight-${i}`}>
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-saffron/10 text-saffron"><h.icon className="h-5 w-5" /></span>
              <div>
                <p className="font-heading text-sm font-extrabold text-navy sm:text-base">{h.v}</p>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{h.l}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INDIA MAP */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24" data-testid="domestic-map-section">
        <SectionHead kicker="Courier Across India" title="From Your Doorstep to Anywhere in India"
                     sub="One network connecting metros, towns and remote pincodes — with routes between every major Indian city." />
        <div className="relative mt-10 overflow-hidden rounded-3xl border border-slate-200 bg-navy shadow-xl">
          <img src={MAP_IMG} alt="IndiaFast delivery route network across India" loading="lazy" className="h-72 w-full object-cover opacity-90 sm:h-[420px]" />
          <span className="route-runner absolute z-10 grid h-8 w-8 place-items-center rounded-full bg-saffron text-white shadow-lg shadow-saffron/50">
            <Package className="h-4 w-4" />
          </span>
          <div className="absolute inset-x-0 bottom-0 flex flex-wrap gap-2 bg-gradient-to-t from-navy/95 to-transparent p-5 pt-12">
            {CITIES.map((c) => (
              <span key={c} className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-bold text-white backdrop-blur">{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="domestic-services">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead kicker="Services" title="One Network. Every Kind of Shipment." />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <div key={i} data-testid={`domestic-service-${i}`}
                   className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl">
                <div className="relative h-36 overflow-hidden">
                  <img src={s.img} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute left-4 top-4 grid h-10 w-10 place-items-center rounded-xl bg-navy/90 text-saffron backdrop-blur"><s.icon className="h-5 w-5" /></span>
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg font-bold text-navy">{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20" data-testid="domestic-how">
        <SectionHead kicker="How It Works" title="Booked in Minutes. Delivered With Care." center />
        <StepsLine steps={STEPS} testid="domestic-steps" />
      </section>

      {/* WHY INDIAFAST */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="domestic-why">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead kicker="Why IndiaFast" title="Built Around Your Shipment" />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map((w, i) => (
              <div key={i} data-testid={`domestic-why-${i}`}
                   className="group rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-all hover:-translate-y-1 hover:border-saffron/40 hover:bg-white hover:shadow-xl">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-navy text-saffron transition-colors group-hover:bg-saffron group-hover:text-white"><w.icon className="h-6 w-6" /></span>
                <h3 className="mt-4 font-heading text-base font-bold text-navy">{w.title}</h3>
                <p className="mt-1.5 text-sm text-slate-500">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCta testid="domestic-final-cta"
                img="https://images.pexels.com/photos/6682787/pexels-photo-6682787.jpeg?auto=compress&cs=tinysrgb&w=1920"
                title="Ready to Send Across India?" primaryLabel="Get a Quote" primaryTo="/rate-calculator" primaryTestid="domestic-final-quote"
                secondary={<a href="/track" data-testid="domestic-final-track" className="flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10"><Search className="h-4 w-4" /> Track Shipment</a>} />
      <Footer contact={contact} />
    </div>
  );
}
